import { useEffect, useLayoutEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useDarkModeCheck } from "../DashboardLayout";
import { GET_WEIGHTING_DATA } from "../../../service/graphql/query";
import { logout } from "../../../service/graphql/utils";
import {
  genericDocumentsTable,
  SortOrder,
} from "../../../service/utils/constants";
import { SortSettings } from "../../../component/sortable-table/SortableTable";
import { sortTableFunction } from "../../../service/utils/functions";
import WeightingPageUI from "../../../ui/dashboard/weighting/WeightingPageUI";
import { useCustomerSelection } from "../../../service/hooks/customerData";
import { auditsDataFormat } from "../../../service/utils/parsers";

const WeightingPage = () => {
  const navigate = useNavigate();
  const { darkMode, userCustomers } = useDarkModeCheck();

  const [headerLine, setHeaderLine] = useState<string[]>([]);
  const [content, setContent] = useState<any[][]>([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState("");
  const [reordering, setReordering] = useState(false);

  const [canViewPage, setCanViewPage] = useState(false);
  const { customerList, selectedCustomer, setSelectedCustomer } =
    useCustomerSelection(canViewPage ? userCustomers : undefined);

  const [width, setWidth] = useState(0);

  const [getWeightingData, { data: weightingData }] =
    useLazyQuery(GET_WEIGHTING_DATA);

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  useEffect(() => {
    if (!userCustomers) return;
    const user = userCustomers[0];
    if (user) {
      if (user.category === "Tipping" || !user.pageAuth.weightingPage) {
        window.history.replaceState(null, "", "/");
        navigate("/dashboard/home");
      } else setCanViewPage(true);
    } else logout(navigate);
  }, [userCustomers, navigate]);

  useEffect(() => {
    setHeaderLine(genericDocumentsTable);

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  // Weighing reports are fetched for the selected customer only, on page
  // open and on every customer change.
  useEffect(() => {
    if (!selectedCustomer?.value) return;
    setContent([]);
    getWeightingData({
      variables: { customerId: selectedCustomer.value },
      context: { apiName: "middleware" },
    });
  }, [selectedCustomer, getWeightingData]);

  useEffect(() => {
    const customerData = weightingData?.getCustomers?.[0];
    if (customerData) {
      setContent(auditsDataFormat(customerData.weightingPlatform ?? []));
    }
  }, [weightingData]);

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      const reorderedTable = content.sort((a, b) =>
        sortTableFunction(a, b, sortSettings)
      );
      setContent(reorderedTable);
    }
  }, [sortSettings, reordering, content]);

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  return (
    <WeightingPageUI
      customerList={customerList}
      headerLine={headerLine}
      content={content}
      sortSettings={sortSettings}
      searchParam={searchParam}
      changeSortAction={changeSortAction}
      changeSearchParam={changeSearchParam}
      resetTable={resetTable}
      setSelectedCustomer={setSelectedCustomer}
      width={width}
      darkMode={darkMode}
    />
  );
};

export default WeightingPage;
